'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { MouseEventHandler, useState } from 'react';
import { FieldError, useFormContext } from 'react-hook-form';

import { Launchpool } from '@flipster/apis/v1/launchpool/types';
import { useLaunchpool } from '@flipster/features/src/shared/launchpool/state';
import { useSnapshotSpec } from '@flipster/features/src/shared/snapshot/use-snapshot-spec';
import { useDepositModal } from '@flipster/features/src/shared/wallet-modal/deposit';
import { FormattedMessage } from '@flipster/i18n';
import { useOverlay } from '@flipster/overlay';
import { walletsBunja } from '@flipster/r230522/original/wallet';
import { Modal } from '@flipster/react/design-system-legacy/modal';
import { Button } from '@flipster/react/design-system/button';
import { Checkbox } from '@flipster/react/design-system/checkbox';
import { snackbar } from '@flipster/react/design-system/snackbar';
import Formatted from '@flipster/react/design-utils/formatted';
import { StepModel } from '@flipster/react/misc/step/use-steps';
import { css } from '@flipster/styled-system/css';
import { flex } from '@flipster/styled-system/patterns';
import { DefaultNumberInputMaxLength } from '@flipster/ui';
import { alert } from '@flipster/ui/Alert';
import { UsdtPrecision } from '@flipster/utils/constants/currency';
import Num, { BigNumber, inputFormatter } from '@flipster/utils/num';
import segment from '@flipster/utils/segment';
import { StakeEvent } from '@flipster/utils/segment/events/stake';

import PriceRateButtons from '@web/components/PriceRateButtons';
import SendPriceInput from '@web/components/SendPriceInput';
import { useUserPoint } from '@web/hooks/usePoint';

import { Steps } from '..';
import SelectActionModal from '../../SelectActionModal';
import { StakingFormSchema } from '../../validation';
import { FormItem } from '../form-item';
import StakingCurrencyTradabilityInfo from '../staking-currency-tradability-info';

const RateItems = [
  { label: '25%', value: '25' },
  { label: '50%', value: '50' },
  { label: '75%', value: '75' },
  { label: '100%', value: '100' },
];

const StakingFormError = ({ error, onClickAddCrypto }: { error?: FieldError; onClickAddCrypto?: () => void }) => {
  if (!error) return null;
  if (error.type === 'insufficient' && onClickAddCrypto) {
    return (
      <div>
        {error.message}
        <button
          type='button'
          onClick={onClickAddCrypto}
          className={css({ textStyle: 'Text/M/Underline', color: 'content.state.negative', ml: 4 })}
        >
          <FormattedMessage id='Autogen_common.r251126.s_Add_crypto' />
        </button>
      </div>
    );
  }
  return error.message;
};

interface Props {
  pool: Launchpool;
  onClose: () => void;
  setModalStep: (step: StepModel<Steps>) => void;
}

const StakingForm = ({ pool, onClose, setModalStep }: Props) => {
  const t = useTranslations();
  const overlay = useOverlay();
  const {
    setValue,
    getValues,
    handleSubmit,
    formState: { errors, isValid, isDirty, isSubmitting },
  } = useFormContext<StakingFormSchema>();

  const { walletsAtom } = useBunja(walletsBunja);
  const wallet = useAtomValue(walletsAtom);
  const openDepositModal = useDepositModal();
  const { postLaunchPoolStakeApiMutation } = useLaunchpool();
  const { currenciesKeyByCurrency } = useSnapshotSpec();
  const userPointQuery = useUserPoint();
  const [agree, setAgree] = useState(false);

  const { id: launchpoolId, isSparkLaunchpool, stakingCurrency, minStakableAmount, maxStakableAmount } = pool;
  const stakingCurrencyLabel = isSparkLaunchpool ? 'Sparks' : stakingCurrency;
  const availableAmount =
    (isSparkLaunchpool ? userPointQuery.data?.points : wallet?.[stakingCurrency]?.walletBalanceExclLocked) || '0';
  const stakingCurrencyPrecision = (() => {
    if (isSparkLaunchpool) return 0;
    return currenciesKeyByCurrency?.[stakingCurrency]?.blockchainInfo[0]?.withdrawalPrecision;
  })();
  const total = Object.values(wallet ?? {}).reduce(
    (acc, curr) => acc.plus(Num(curr.walletBalanceWithdrawableInUsdt)),
    Num(0),
  );
  const isNoAvailableAmount = total.isZero();

  const postStake = async (amount: string) => {
    try {
      await postLaunchPoolStakeApiMutation.mutateAsync({
        launchpoolId,
        params: { amount },
      });
      if (isSparkLaunchpool) onClose();
      else setModalStep({ step: 'stakingComplete', context: undefined });
      const message = isSparkLaunchpool ? (
        <FormattedMessage id='Autogen_common.r251126.s_You_have_successfully_joined_the_Launchpool' />
      ) : (
        <FormattedMessage
          id='Autogen_common.r251126.s_currency_successfully_staked'
          values={{ currency: stakingCurrency }}
        />
      );
      snackbar.success({ message });
      segment.track(StakeEvent.StakeConfirmClick, { symbol: stakingCurrency, quantity: amount });
    } catch (error) {
      alert.error(error);
    }
  };

  const submitFlow = () => {
    const amount = getValues('amount');
    if (!(launchpoolId && amount)) return;
    return postStake(amount);
  };

  const onClickPercent: MouseEventHandler<HTMLButtonElement> = (e) => {
    if (!availableAmount) return;
    const { value } = e.currentTarget;
    const calculatedAmount = Num(availableAmount)
      .dividedBy(100)
      .multipliedBy(value)
      .toFixed(stakingCurrencyPrecision ?? UsdtPrecision, BigNumber.ROUND_DOWN);
    const formattedAmount = inputFormatter(calculatedAmount, {
      withComma: true,
      maxLength: DefaultNumberInputMaxLength,
    });
    const withoutComma = inputFormatter(formattedAmount, { withComma: false });
    setValue('amount', withoutComma, { shouldDirty: true, shouldValidate: true });
    segment.track(StakeEvent.StakePercentageClick, { symbol: pool.stakingCurrency, percentage: value });
  };

  const onClickAddCrypto = isSparkLaunchpool
    ? undefined
    : () => {
        onClose();
        if (isNoAvailableAmount) {
          openDepositModal();
        } else {
          overlay.open(({ isOpen, onClose, unmount }) => (
            <SelectActionModal launchpoolId={launchpoolId} isOpen={isOpen} onClose={onClose} onExit={unmount} />
          ));
        }
      };

  return (
    <form
      onSubmit={handleSubmit(submitFlow)}
      className={flex({ flexDirection: 'column', textAlign: 'center', h: '100%' })}
    >
      <Modal.Content>
        <FormItem
          error={<StakingFormError error={errors.amount} onClickAddCrypto={onClickAddCrypto} />}
          className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}
        >
          <SendPriceInput
            name='amount'
            currency={stakingCurrencyLabel}
            max={maxStakableAmount}
            precision={stakingCurrencyPrecision ?? UsdtPrecision}
          />
        </FormItem>
        <p className={css({ textStyle: 'Text/M/Monospace/Regular', color: 'content.default.level-2', mb: 4 })}>
          <FormattedMessage id='Trade.r250401.Available_colon' /> &nbsp;
          <Formatted.Number
            value={availableAmount}
            suffix={stakingCurrencyLabel}
            precision={stakingCurrencyPrecision}
            toFixed
          />
        </p>
        <p className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-2' })}>
          <FormattedMessage
            id='Autogen_common.r251126.s_Staking_limit_arg0_arg1'
            values={{
              arg0: <Formatted.Number value={minStakableAmount} />,
              arg1: <Formatted.Number value={maxStakableAmount} />,
            }}
          />
        </p>
        <StakingCurrencyTradabilityInfo stakingCurrency={stakingCurrency} />
        <div
          className={css({
            position: { base: 'fixed', tablet: 'static' },
            bottom: 68,
            left: 0,
            right: 0,
            py: 12,
            px: 16,
            mt: 16,
          })}
        >
          <PriceRateButtons
            items={RateItems}
            onClick={onClickPercent}
            className={css({ bgColor: 'background.default.level-3!' })}
          />
        </div>
        {isSparkLaunchpool && (
          <Checkbox
            css={{ mt: 16, alignSelf: 'center', textAlign: 'left', textStyle: 'Text/M/Regular' }}
            checked={agree}
            onChange={(e) => setAgree(e.target.checked)}
          >
            <FormattedMessage id='Autogen_common.r251126.s_I_agree_to_permanently_burning_my_Sparks' />
          </Checkbox>
        )}
      </Modal.Content>
      <Modal.Footer>
        {/* 해당 버튼은 에러의 Add crypto 버튼과 다르게, 잔고가 0일때만 표시함 */}
        {isNoAvailableAmount && !isSparkLaunchpool ? (
          <Button
            label={t('Autogen_common.r251126.s_Add_crypto')}
            size='large'
            hierarchy='primary'
            color='neutral'
            onClick={() => {
              onClose();
              openDepositModal();
            }}
          />
        ) : (
          <Button
            htmlType='submit'
            disabled={!isValid || !isDirty || (isSparkLaunchpool && !agree)}
            isLoading={isSubmitting}
            label={t('Autogen_common.r251126.s_Confirm')}
          />
        )}
      </Modal.Footer>
    </form>
  );
};

export default StakingForm;
